import {
  ArrowLongLeftIcon,
  Button,
  Divider,
  Flex,
  InputContainer,
  Menu,
  Text,
  TextField,
  toaster,
} from 'components'
import { lowerCase, startCase } from 'lodash-es'
import { useEffect, useState } from 'react'
import { LookupOutputProp, LookupOutputProps } from 'types'
import { readSpreadsheet, sheetToJson } from 'utils'
import SmartColumnAdditionalMapping from './SmartColumnAdditionalMapping'

interface Props {
  inputsDisabled: boolean
  dbTabs: string[]
  setRawDbData: (data: Record<string, string>[]) => void
  dbFields?: string[]
  setDbFields: (fields: string[]) => void
  dbInputProps?: string[]
  setDbInputProps: (dbInputProp: string[]) => void
  lookupOutputProps: LookupOutputProp[]
  setLookupOutputProps: React.Dispatch<React.SetStateAction<LookupOutputProp[]>>
  buffer?: ArrayBuffer
  momentifyFields: string[]
  setSaveEnabled: (enable: boolean) => void
  pageIsLoading: (loading: boolean) => void
}

export default function SmartColumnDataMapper({
  inputsDisabled,
  dbTabs,
  setRawDbData,
  dbFields,
  setDbFields,
  dbInputProps,
  setDbInputProps,
  lookupOutputProps,
  setLookupOutputProps,
  momentifyFields,
  buffer,
  setSaveEnabled,
  pageIsLoading,
}: Props) {
  const [selectedTab, setSelectedTab] = useState('')
  const [nextId, setNextId] = useState(1)
  const rowLimit = 100000

  useEffect(() => {
    if (dbTabs.length === 1) setSelectedTab(dbTabs[0])
  }, [dbTabs])

  useEffect(() => {
    dbTabs
  }, [buffer])

  useEffect(() => {
    if (!selectedTab || !buffer) return
    ;(async () => {
      try {
        pageIsLoading(true)
        const { Sheets } = await readSpreadsheet(buffer)
        const sheet = Sheets[selectedTab]
        const data = await sheetToJson(sheet)

        if (data.length > rowLimit) {
          toaster.create({
            type: 'error',
            title: `File exceeds limit of ${rowLimit} rows`,
          })
          return
        }

        setRawDbData(data)
        setDbFields(Object.keys(data[0]))
        pageIsLoading(false)
      } catch (err) {
        console.error('Error processing spreadsheet:', err)
      }
    })()
  }, [selectedTab])

  function handleAddField() {
    setLookupOutputProps(prev => [
      ...(prev || []),
      { index: nextId, dbField: '', dbFieldName: '', hasChart: false },
    ])
    setNextId(prev => prev + 1)
  }

  function handleRemoveRow(rowId: number) {
    setLookupOutputProps(prev => prev.filter(row => row.index !== rowId))
  }

  function inputFieldCheck(value: string) {
    if (momentifyFields.map(field => lowerCase(String(field))).includes(lowerCase(value))) {
      toaster.create({
        title: 'This name is already being used for a Momentify field. Enter a unique field name.',
        type: 'error',
      })
      setSaveEnabled(false)
    } else setSaveEnabled(true)
  }

  function updateOutputProps({ index, updateOn, dbField, fieldName }: LookupOutputProps) {
    const updatedOutputProps = lookupOutputProps.map(row => {
      if (row.index !== index) return row

      switch (updateOn) {
        case 'field':
          return dbField ? { ...row, dbField } : row

        case 'fieldName':
          return fieldName ? { ...row, dbFieldName: fieldName } : row

        case 'hasChart':
          return { ...row, hasChart: !row.hasChart }

        default:
          return row
      }
    })

    setLookupOutputProps(updatedOutputProps)
  }

  return (
    <Flex direction="column" gap={3}>
      <Divider />
      {dbTabs && (
        <Flex gap={5} align="center">
          <Text textStyle="bodySmall" opacity={0.7}>
            Database Tab:
          </Text>
          <Menu
            variant="outline"
            borderRadius="10px"
            menuProps={{ positioning: { sameWidth: true } }}
            disabled={inputsDisabled}
            value={selectedTab}
            options={dbTabs.map(tab => ({ label: tab, value: tab }))}
            onChange={value => setSelectedTab(value)}
          />
        </Flex>
      )}
      {dbFields && (
        <Flex direction="column" gap={3}>
          <Flex align="flex-end" gap={5} pb={1}>
            <Menu
              variant="outline"
              menuProps={{
                positioning: { sameWidth: true },
              }}
              w={optionWidths}
              borderRadius="10px"
              label="Database Fields"
              disabled={inputsDisabled}
              value={dbInputProps}
              isMulti
              options={dbFields.map(property => ({
                type: 'item',
                label: startCase(property),
                value: property,
              }))}
              onChange={values => {
                setDbInputProps(values)
              }}
            />
            <ArrowLongLeftIcon transform="rotateY(180deg)" mb={2} />
            <Menu
              variant="outline"
              menuProps={{
                positioning: { sameWidth: true },
              }}
              disabled={!dbInputProps || inputsDisabled}
              w={optionWidths}
              borderRadius="10px"
              label="Mapped Field"
              value={lookupOutputProps[0] ? lookupOutputProps[0].dbField : ''}
              options={dbFields
                .filter(key => !dbInputProps?.includes(key))
                .map(property => ({
                  type: 'item',
                  label: startCase(property),
                  value: property,
                }))}
              onChange={value => {
                updateOutputProps({ index: 0, updateOn: 'field', dbField: value })
              }}
            />
            <InputContainer label="New Mapped Field Name" px={1}>
              <TextField
                size="sm"
                h="40px"
                disabled={!lookupOutputProps[0] || inputsDisabled}
                w={optionWidths}
                borderRadius="10px"
                placeholder="Enter field name..."
                defaultValue={lookupOutputProps[0].dbFieldName!}
                onChange={e => {
                  inputFieldCheck(e.target.value)
                  updateOutputProps({
                    index: 0,
                    updateOn: 'fieldName',
                    fieldName: e.target.value,
                  })
                }}
              />
            </InputContainer>
          </Flex>
          {lookupOutputProps.slice(1).map(row => (
            <SmartColumnAdditionalMapping
              key={row.index}
              rowKey={row.index}
              dbInputProps={dbInputProps!}
              lookupOutputProps={lookupOutputProps}
              dbFields={dbFields}
              disabled={inputsDisabled}
              handleRemoveRow={handleRemoveRow}
              updateOutputProps={updateOutputProps}
              inputFieldCheck={inputFieldCheck}
            />
          ))}
          {lookupOutputProps[lookupOutputProps.length - 1].dbField &&
            lookupOutputProps.length < dbFields.length - 1 && (
              <Button
                variant="outline"
                w={optionWidths}
                ml={{ base: 'auto', lg: '314px' }}
                onClick={handleAddField}
                disabled={inputsDisabled}
              >
                Add Field
              </Button>
            )}
        </Flex>
      )}
    </Flex>
  )
}

const optionWidths = { base: '100%', sm: '250px' }
